/**
 * Admin Webinar Management Page
 * CRUD operations for webinars, recordings, and speakers
 * Access: super_admin, ops_admin only
 */

import React, { useState, useEffect } from 'react';
import {
  Plus,
  Edit,
  Trash2,
  Video,
  Calendar,
  Users,
  ExternalLink,
  Play,
  Eye,
  EyeOff,
  Star,
  Clock,
  CheckCircle,
  XCircle,
  Loader2,
  ArrowLeft,
  Copy,
  Upload
} from 'lucide-react';
import {
  listWebinars,
  createWebinar,
  updateWebinar,
  deleteWebinar,
  publishRecording,
  Webinar,
  WebinarCreate,
  WebinarUpdate
} from '@/services/api/webinarManagement';
import { PageHeader, PageContainer } from '@/components/weavein';

type ModalMode = 'create' | 'edit' | 'recording' | null;

export default function WebinarManagementPage() {
  const [webinars, setWebinars] = useState<Webinar[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [modalMode, setModalMode] = useState<ModalMode>(null);
  const [selectedWebinar, setSelectedWebinar] = useState<Webinar | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [saving, setSaving] = useState(false);

  // Form state
  const [formData, setFormData] = useState<WebinarCreate & { recording_url?: string; recording_duration_minutes?: number; recording_thumbnail_url?: string }>({
    title: '',
    description: '',
    event_date: '',
    event_time: '',
    timezone: 'America/Los_Angeles',
    duration_minutes: 60,
    zoom_webinar_link: '',
    zoom_webinar_id: '',
    zoom_passcode: '',
    status: 'draft',
    is_featured: false,
    max_attendees: undefined,
    tags: []
  });

  useEffect(() => {
    loadWebinars();
  }, [statusFilter]);

  const loadWebinars = async () => {
    try {
      setLoading(true);
      const response = await listWebinars(statusFilter || undefined);
      setWebinars(response.webinars);
      setError(null);
    } catch (err: any) {
      console.error('Failed to load webinars:', err);
      setError(err.response?.data?.detail || 'Failed to load webinars');
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({
      title: '',
      description: '',
      event_date: '',
      event_time: '10:00',
      timezone: 'America/Los_Angeles',
      duration_minutes: 60,
      zoom_webinar_link: '',
      zoom_webinar_id: '',
      zoom_passcode: '',
      status: 'draft',
      is_featured: false,
      max_attendees: undefined,
      tags: []
    });
    setSelectedWebinar(null);
    setModalMode('create');
  };

  const openEditModal = (webinar: Webinar) => {
    setFormData({
      title: webinar.title,
      description: webinar.description || '',
      event_date: webinar.event_date,
      event_time: webinar.event_time,
      timezone: webinar.timezone || 'America/Los_Angeles',
      duration_minutes: webinar.duration_minutes || 60,
      zoom_webinar_link: webinar.zoom_link || '',
      zoom_webinar_id: webinar.zoom_webinar_id || '',
      zoom_passcode: webinar.zoom_passcode || '',
      status: webinar.status as 'draft' | 'published',
      is_featured: webinar.is_featured,
      max_attendees: webinar.max_attendees,
      tags: webinar.tags || []
    });
    setSelectedWebinar(webinar);
    setModalMode('edit');
  };

  const openRecordingModal = (webinar: Webinar) => {
    setFormData({
      ...formData,
      recording_url: webinar.recording_url || '',
      recording_duration_minutes: webinar.recording_duration_minutes || undefined,
      recording_thumbnail_url: webinar.recording_thumbnail_url || ''
    });
    setSelectedWebinar(webinar);
    setModalMode('recording');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    try {
      if (modalMode === 'create') {
        await createWebinar(formData);
      } else if (modalMode === 'edit' && selectedWebinar) {
        await updateWebinar(selectedWebinar.id, formData as WebinarUpdate);
      } else if (modalMode === 'recording' && selectedWebinar) {
        await publishRecording(
          selectedWebinar.id,
          formData.recording_url || '',
          formData.recording_duration_minutes,
          formData.recording_thumbnail_url
        );
      }

      setModalMode(null);
      loadWebinars();
    } catch (err: any) {
      console.error('Save error:', err);
      setError(err.response?.data?.detail || 'Failed to save webinar');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (webinar: Webinar) => {
    if (!confirm(`Are you sure you want to delete "${webinar.title}"?`)) return;

    try {
      await deleteWebinar(webinar.id);
      loadWebinars();
    } catch (err: any) {
      console.error('Delete error:', err);
      setError(err.response?.data?.detail || 'Failed to delete webinar');
    }
  };

  const copyZoomLink = (link: string) => {
    navigator.clipboard.writeText(link);
    // Could add a toast notification here
  };

  const getStatusBadge = (status: string) => {
    const badges: Record<string, { bg: string; text: string; icon: React.ReactNode }> = {
      draft: { bg: 'bg-paper-2', text: 'text-ink-700', icon: <EyeOff className="w-3 h-3" /> },
      published: { bg: 'bg-green-100', text: 'text-green-700', icon: <Eye className="w-3 h-3" /> },
      cancelled: { bg: 'bg-red-100', text: 'text-red-700', icon: <XCircle className="w-3 h-3" /> },
      completed: { bg: 'bg-blue-100', text: 'text-blue-700', icon: <CheckCircle className="w-3 h-3" /> }
    };
    const badge = badges[status] || badges.draft;
    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
        {badge.icon}
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        eyebrow="Admin · Content"
        title="Webinar Management"
        description="Create, edit, and manage webinars and recordings"
        actions={
          <>
            <button
              onClick={() => {
                localStorage.setItem('activeTab', 'dashboard');
                window.dispatchEvent(new Event('storage'));
              }}
              className="inline-flex items-center gap-1.5 rounded-md border border-v2border bg-surface px-2.5 py-1.5 text-xs font-medium text-ink hover:bg-paper-2"
            >
              <ArrowLeft className="w-4 h-4" />
              Back
            </button>
            <button
              onClick={openCreateModal}
              className="inline-flex items-center gap-1.5 rounded-md bg-v2accent px-3 py-1.5 text-xs font-medium text-white hover:opacity-90"
            >
              <Plus className="w-4 h-4" />
              New Webinar
            </button>
          </>
        }
      />
      <PageContainer>

      {/* Filters */}
      <div>
        <div className="flex items-center gap-4">
          <label className="text-sm font-medium text-ink-700">Filter by Status:</label>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-v2border rounded-lg bg-surface text-ink text-sm"
          >
            <option value="">All</option>
            <option value="draft">Draft</option>
            <option value="published">Published</option>
            <option value="completed">Completed</option>
            <option value="cancelled">Cancelled</option>
          </select>
          <span className="text-sm text-ink-500">
            {webinars.length} webinar{webinars.length !== 1 ? 's' : ''}
          </span>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div>
          <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
            {error}
          </div>
        </div>
      )}

      {/* Webinars Table */}
      <div>
        <div className="bg-surface rounded-xl shadow overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#5B4B8A]" />
            </div>
          ) : webinars.length === 0 ? (
            <div className="text-center py-12">
              <Video className="w-12 h-12 text-ink-400 mx-auto mb-4" />
              <p className="text-ink-500">No webinars found</p>
              <button
                onClick={openCreateModal}
                className="mt-4 text-[#5CBDB9] hover:text-[#4A9E9B] font-medium"
              >
                Create your first webinar
              </button>
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-paper dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Webinar
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Date & Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Registrations
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Recording
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-ink-500 dark:text-ink-300 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-v2border-muted">
                {webinars.map((webinar) => (
                  <tr key={webinar.id} className="hover:bg-paper-2/50">
                    <td className="px-6 py-4">
                      <div className="flex items-start gap-3">
                        {webinar.is_featured && (
                          <Star className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-1" />
                        )}
                        <div>
                          <p className="font-medium text-ink">
                            {webinar.title}
                          </p>
                          <p className="text-sm text-ink-500">
                            {webinar.event_id}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex items-center gap-2 text-sm">
                        <Calendar className="w-4 h-4 text-ink-400" />
                        <span className="text-ink">{webinar.event_date}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-ink-500">
                        <Clock className="w-4 h-4 text-ink-400" />
                        {webinar.event_time}
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(webinar.status)}
                    </td>
                    <td className="px-6 py-4">
                      <button
                        onClick={() => {
                          // Store selected event for the registrations page
                          localStorage.setItem('selected_webinar_event', webinar.event_id);
                          localStorage.setItem('activeTab', 'event-registrations');
                          // Trigger storage event for App to pick up
                          window.dispatchEvent(new Event('storage'));
                        }}
                        className="flex items-center gap-2 text-[#5CBDB9] hover:text-[#4A9E9B]"
                      >
                        <Users className="w-4 h-4" />
                        <span className="font-medium">
                          {webinar.registration_count || 0}
                        </span>
                      </button>
                    </td>
                    <td className="px-6 py-4">
                      {webinar.has_recording ? (
                        <span className="inline-flex items-center gap-1 text-green-600">
                          <Play className="w-4 h-4" />
                          Available
                        </span>
                      ) : (
                        <button
                          onClick={() => openRecordingModal(webinar)}
                          className="text-sm text-[#5CBDB9] hover:text-[#4A9E9B] flex items-center gap-1"
                        >
                          <Upload className="w-4 h-4" />
                          Add Recording
                        </button>
                      )}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        {webinar.zoom_link && (
                          <button
                            onClick={() => copyZoomLink(webinar.zoom_link!)}
                            className="p-2 text-ink-400 hover:text-ink-500 dark:hover:text-ink-300"
                            title="Copy Zoom Link"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        )}
                        <button
                          onClick={() => openEditModal(webinar)}
                          className="p-2 text-ink-400 hover:text-[#5B4B8A]"
                          title="Edit"
                        >
                          <Edit className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => handleDelete(webinar)}
                          className="p-2 text-ink-400 hover:text-red-600"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Modal */}
      {modalMode && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-v2border">
              <h2 className="text-xl font-bold text-ink">
                {modalMode === 'create' && 'Create New Webinar'}
                {modalMode === 'edit' && 'Edit Webinar'}
                {modalMode === 'recording' && 'Add Recording'}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {modalMode !== 'recording' ? (
                <>
                  {/* Title */}
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Title <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      placeholder="e.g., Webinar: The Future of Employer Benefits"
                    />
                  </div>

                  {/* Description */}
                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Description
                    </label>
                    <textarea
                      rows={3}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      placeholder="Brief description of the webinar..."
                    />
                  </div>

                  {/* Date & Time */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">
                        Date <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="date"
                        required
                        value={formData.event_date}
                        onChange={(e) => setFormData({ ...formData, event_date: e.target.value })}
                        className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">
                        Time <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="time"
                        required
                        value={formData.event_time}
                        onChange={(e) => setFormData({ ...formData, event_time: e.target.value })}
                        className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      />
                    </div>
                  </div>

                  {/* Timezone & Duration */}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">
                        Timezone
                      </label>
                      <select
                        value={formData.timezone}
                        onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                        className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      >
                        <option value="America/Los_Angeles">Pacific (PT)</option>
                        <option value="America/Denver">Mountain (MT)</option>
                        <option value="America/Chicago">Central (CT)</option>
                        <option value="America/New_York">Eastern (ET)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-ink-700 mb-1">
                        Duration (minutes)
                      </label>
                      <input
                        type="number"
                        value={formData.duration_minutes}
                        onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                        className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      />
                    </div>
                  </div>

                  {/* Zoom Details */}
                  <div className="border-t border-v2border pt-4 mt-4">
                    <h3 className="text-sm font-semibold text-ink mb-3 flex items-center gap-2">
                      <Video className="w-4 h-4" />
                      Zoom Webinar Details
                    </h3>
                    <div className="space-y-3">
                      <div>
                        <label className="block text-sm font-medium text-ink-700 mb-1">
                          Zoom Webinar Link
                        </label>
                        <input
                          type="url"
                          value={formData.zoom_webinar_link}
                          onChange={(e) => setFormData({ ...formData, zoom_webinar_link: e.target.value })}
                          className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                          placeholder="https://us06web.zoom.us/j/..."
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-ink-700 mb-1">
                            Webinar ID
                          </label>
                          <input
                            type="text"
                            value={formData.zoom_webinar_id}
                            onChange={(e) => setFormData({ ...formData, zoom_webinar_id: e.target.value })}
                            className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                            placeholder="123 456 7890"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-ink-700 mb-1">
                            Passcode
                          </label>
                          <input
                            type="text"
                            value={formData.zoom_passcode}
                            onChange={(e) => setFormData({ ...formData, zoom_passcode: e.target.value })}
                            className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                            placeholder="Optional"
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Status & Options */}
                  <div className="border-t border-v2border pt-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-ink-700 mb-1">
                          Status
                        </label>
                        <select
                          value={formData.status}
                          onChange={(e) => setFormData({ ...formData, status: e.target.value as 'draft' | 'published' })}
                          className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                        >
                          <option value="draft">Draft (Hidden)</option>
                          <option value="published">Published (Visible)</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-ink-700 mb-1">
                          Max Attendees
                        </label>
                        <input
                          type="number"
                          value={formData.max_attendees || ''}
                          onChange={(e) => setFormData({ ...formData, max_attendees: e.target.value ? parseInt(e.target.value) : undefined })}
                          className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                          placeholder="Unlimited"
                        />
                      </div>
                    </div>
                    <div className="mt-3">
                      <label className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={formData.is_featured}
                          onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })}
                          className="w-4 h-4 text-[#5CBDB9] border-v2border rounded focus:ring-[#5CBDB9]"
                        />
                        <span className="text-sm text-ink-700">
                          Feature this webinar (shows at top with star)
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              ) : (
                /* Recording Form */
                <>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg mb-4">
                    <p className="text-sm text-blue-800 dark:text-blue-300">
                      Adding a recording will automatically mark this webinar as "Completed" and make the recording available on the Resources page.
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Recording URL <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="url"
                      required
                      value={formData.recording_url}
                      onChange={(e) => setFormData({ ...formData, recording_url: e.target.value })}
                      className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      placeholder="https://youtube.com/watch?v=... or Zoom cloud recording URL"
                    />
                    <p className="text-xs text-ink-500 mt-1">
                      YouTube, Vimeo, or Zoom cloud recording link
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Recording Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={formData.recording_duration_minutes || ''}
                      onChange={(e) => setFormData({ ...formData, recording_duration_minutes: e.target.value ? parseInt(e.target.value) : undefined })}
                      className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      placeholder="e.g., 45"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-ink-700 mb-1">
                      Thumbnail URL
                    </label>
                    <input
                      type="url"
                      value={formData.recording_thumbnail_url}
                      onChange={(e) => setFormData({ ...formData, recording_thumbnail_url: e.target.value })}
                      className="w-full px-4 py-2.5 border border-v2border rounded-lg bg-surface text-ink"
                      placeholder="https://... (optional)"
                    />
                  </div>
                </>
              )}

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-v2border">
                <button
                  type="button"
                  onClick={() => setModalMode(null)}
                  className="px-4 py-2 text-ink-700 hover:bg-paper-2 dark:hover:bg-gray-800 rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-6 py-2 bg-[#5CBDB9] text-white rounded-lg hover:bg-[#4A9E9B] transition-colors font-medium disabled:opacity-50 flex items-center gap-2"
                >
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                  {modalMode === 'create' && 'Create Webinar'}
                  {modalMode === 'edit' && 'Save Changes'}
                  {modalMode === 'recording' && 'Publish Recording'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
      </PageContainer>
    </div>
  );
}
